import { useCallback } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { IDIOMA_POR_DEFECTO, IDIOMAS, tiempoRelativo, traducir } from "i18n/diccionario";

/**
 * Idioma de la interfaz.
 *
 * Se guarda en el navegador de cada persona: cada usuario elige el suyo y no
 * afecta a los demás. La primera vez se intenta adivinar desde el idioma del
 * navegador, así alguien que llega con Chrome en chino ve la interfaz en chino
 * sin tener que buscar el selector.
 */

const CODIGOS = IDIOMAS.map((i) => i.codigo);

function idiomaDelNavegador() {
  if (typeof navigator === "undefined") return IDIOMA_POR_DEFECTO;
  const preferidos = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const bruto of preferidos) {
    if (!bruto) continue;
    // "zh-CN", "zh-Hans", "zh" → "zh"
    const corto = String(bruto).toLowerCase().split("-")[0];
    if (CODIGOS.includes(corto)) return corto;
  }
  return IDIOMA_POR_DEFECTO;
}

export const useIdiomaStore = create(
  persist(
    (set) => ({
      idioma: idiomaDelNavegador(),
      cambiarIdioma: (codigo) => {
        if (!CODIGOS.includes(codigo)) return;
        set({ idioma: codigo });
        if (typeof document !== "undefined") {
          // Para que el navegador y los lectores de pantalla sepan en qué
          // idioma está la página.
          document.documentElement.lang = codigo;
        }
      },
    }),
    {
      name: "dte-nrs-idioma",
      // Si el navegador bloquea el almacenamiento (modo privado, políticas de
      // empresa), la aplicación sigue funcionando con el idioma detectado.
      onRehydrateStorage: () => (estado) => {
        if (estado?.idioma && typeof document !== "undefined") {
          document.documentElement.lang = estado.idioma;
        }
      },
    },
  ),
);

/**
 * Hook para usar en las pantallas:
 *   const { t } = useIdioma();
 *   <th>{t("col.folio")}</th>
 */
export function useIdioma() {
  const idioma = useIdiomaStore((s) => s.idioma);
  const cambiarIdioma = useIdiomaStore((s) => s.cambiarIdioma);

  // `t` se memoiza contra el idioma: así mantiene la misma identidad entre
  // renders y las pantallas que la usan como dependencia de un useMemo solo
  // recalculan cuando el idioma cambia de verdad, no en cada render.
  // `respaldo` es el texto en español que ya estaba escrito a mano en la
  // pantalla. Si falta la traducción se muestra eso, nunca la clave cruda.
  const t = useCallback((clave, respaldo) => traducir(clave, idioma, respaldo), [idioma]);

  // "hace 2 h" / "2 h ago" / "2 小时前"
  const tRelativo = useCallback(
    (cantidad, claveUnidad) => tiempoRelativo(cantidad, claveUnidad, idioma),
    [idioma],
  );

  return { idioma, cambiarIdioma, idiomas: IDIOMAS, t, tRelativo };
}
